import api from './api';
import {
  StartChatResponse,
  ChatMessageResponse,
  Conversation,
  ConversationListItem,
} from '../types';

export interface Symptom {
  id: string;
  name: string;
  description: string;
}

export interface SymptomCategory {
  name: string;
  symptoms: Symptom[];
}

export interface SymptomCategories {
  [key: string]: SymptomCategory;
}

export interface ProbableCondition {
  condition: string;
  probability: 'High' | 'Medium' | 'Low';
  matchingSymptoms: number;
  description: string;
}

export interface CareRecommendation {
  type: 'Self-Care' | 'Monitor' | 'Schedule Appointment' | 'Urgent Care' | 'Emergency';
  recommendation: string;
  specialist?: string;
}

export interface SymptomAnalysisResult {
  probableConditions: ProbableCondition[];
  severity: 'Mild' | 'Moderate' | 'Severe';
  careRecommendations: CareRecommendation[];
  analysis: string;
  disclaimer: string;
}

// Nutrition Planner Types
export type WeightGoal = 'weight_loss' | 'weight_gain' | 'maintain';
export type Intensity = 'gentle' | 'normal' | 'aggressive';
export type PlanDuration = 4 | 6 | 8;

export interface NutritionPlannerMetadata {
  weightGoals: Array<{ value: WeightGoal; label: string; description: string }>;
  intensityOptions: Array<{ value: Intensity; label: string; description: string; weeklyAdjustment: number }>;
  durationOptions: Array<{ weeks: number; label: string; description: string }>;
  activityMultipliers: Record<string, { label: string; multiplier: number; description: string }>;
}

export interface MaintenanceCalories {
  bmr: number;
  tdee: number;
  formula: string;
  activityMultiplier: number;
}

export interface MacroBreakdown {
  proteinG: number;
  carbsG: number;
  fatG: number;
}

export interface WeeklyPlan {
  week: number;
  dailyCalories: number;
  adjustmentFromMaintenance: number;
  cumulativeAdjustment: number;
  expectedWeightChange: number;
  macros: MacroBreakdown;
}

export interface SafetyWarning {
  level: 'info' | 'caution' | 'warning';
  message: string;
}

export interface PreConditionImpact {
  condition: string;
  icd10Code: string;
  impact: string;
  dietaryRecommendation: string;
  adjustmentFactor: number;
}

export interface NutritionPlanResult {
  profileSummary: {
    age: number;
    gender: string;
    height: number;
    weight: number;
    activityLevel: string;
    bmi: number;
    bmiCategory: string;
  };
  maintenanceCalories: MaintenanceCalories;
  weightGoal: WeightGoal;
  intensity: Intensity;
  intensityDescription: string;
  durationWeeks: number;
  weeklyPlan: WeeklyPlan[];
  preConditionImpacts: PreConditionImpact[];
  safetyWarnings: SafetyWarning[];
  expectedOutcome: {
    totalWeeks: number;
    startingCalories: number;
    finalCalories: number;
    expectedWeightChange: number;
    expectedFinalWeight: number;
  };
  conversationId?: string;
  disclaimer: string;
}

export const chatService = {
  /**
   * Start a new conversation
   */
  async startConversation(): Promise<StartChatResponse> {
    const response = await api.post<StartChatResponse>('/chat/start');
    return response.data;
  },

  /**
   * Send a message in a conversation
   */
  async sendMessage(conversationId: string, message: string): Promise<ChatMessageResponse> {
    const response = await api.post<ChatMessageResponse>('/chat/message', {
      conversationId,
      message,
    });
    return response.data;
  },

  /**
   * Get a conversation by ID
   */
  async getConversation(conversationId: string): Promise<Conversation> {
    const response = await api.get<Conversation>(`/chat/${conversationId}`);
    return response.data;
  },

  /**
   * List all conversations
   */
  async listConversations(): Promise<ConversationListItem[]> {
    const response = await api.get<ConversationListItem[]>('/chat');
    return response.data;
  },

  /**
   * Get symptom categories for symptom checker
   */
  async getSymptomCategories(): Promise<SymptomCategories> {
    const response = await api.get<SymptomCategories>('/chat/symptoms/categories');
    return response.data;
  },

  /**
   * Analyze symptoms and get probable conditions
   */
  async analyzeSymptoms(symptoms: string[], pincode?: string, conversationId?: string): Promise<SymptomAnalysisResult> {
    const response = await api.post<SymptomAnalysisResult>('/chat/symptoms/analyze', {
      symptoms,
      pincode,
      conversationId,
    });
    return response.data;
  },

  /**
   * Delete a single conversation
   */
  async deleteConversation(conversationId: string): Promise<void> {
    await api.delete(`/chat/${conversationId}`);
  },

  /**
   * Delete all conversations for the user
   */
  async deleteAllConversations(): Promise<{ deletedCount: number }> {
    console.log('chatService.deleteAllConversations: Starting DELETE /chat/clear-all');
    try {
      const response = await api.delete<{ message: string; deletedCount: number }>('/chat/clear-all');
      console.log('chatService.deleteAllConversations: Success', response.data);
      return { deletedCount: response.data.deletedCount };
    } catch (error: any) {
      console.error('chatService.deleteAllConversations: Error', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  },

  /**
   * Get nutrition planner metadata (goals, intensities, durations)
   */
  async getNutritionMetadata(): Promise<NutritionPlannerMetadata> {
    const response = await api.get<NutritionPlannerMetadata>('/chat/nutrition/metadata');
    return response.data;
  },

  /**
   * Create a personalized nutrition plan
   */
  async createNutritionPlan(
    weightGoal: WeightGoal,
    intensity: Intensity,
    durationWeeks: PlanDuration
  ): Promise<NutritionPlanResult> {
    const response = await api.post<NutritionPlanResult>('/chat/nutrition/plan', {
      weightGoal,
      intensity,
      durationWeeks,
    });
    return response.data;
  },
};
